<script lang="ts">
  import { PlusCircle } from 'phosphor-svelte';
  import { userStore, setUIState, userId } from '$lib/stores/userStore';
  import type { PutUserRequest, GetUserResponse } from '$lib/models/user/types';
  import Button from '$lib/components/common/button/Button.svelte';
  import { getErrorMessage, isErrorResponse } from '$lib/utils/error/errorUtils';
  import { SlideToggle, getToastStore } from '@skeletonlabs/skeleton';
  import { createEventDispatcher, onMount } from 'svelte';

  export let clientId: number;

  const dispatch = createEventDispatcher();
  const toastrStore = getToastStore();

  let name = '';
  let userName = '';
  let email = '';
  let firstPage: string | undefined = '';
  let isExemptedFromSync = false;
  let permissions: string[] = [];
  let isActive = true;

  let availablePermissions: Array<{ key: number; value: string }> = [];
  let loading = false;
  let userPhoto: string | null = null;

  const firstPageOptions = [
    'Create Claim',
    'Invoice',
    'Expense Claim Pipeline',
    'Invoice Upload',
    'Expense Claim',
    'Credit Cards',
    'Purchase Order Pipeline',
    'My Dashboard',
    'My Queue',
    'Create PO',
    'Invoice Pipeline',
    'Purchase Order',
  ].sort();

  async function checkUserState() {
    const userCount = await userStore.count(clientId);
    setUIState(userCount > 0 ? 'DataTable' : 'NoUserPage');
  }

  async function loadUserData(): Promise<void> {
    try {
      const result = await userStore.fetchById($userId ?? 0);
      if (isErrorResponse(result)) {
        toastrStore.trigger({
          message: getErrorMessage(result),
          background: 'bg-error-500',
        });
      } else {
        const user: GetUserResponse = result;
        name = user.name;
        userName = user.userName;
        email = user.email;
        firstPage = user.firstPage;
        permissions = user.permissions?.split(',').map((permission) => permission.trim()) ?? [];
        isExemptedFromSync = user.isExemptedFromSync;
        isActive = user.isActive;

        if (user.userPhoto) {
          userPhoto = `data:image/png;base64,${user.userPhoto}`;
        }
      }
    } catch (errors) {
      for (let error of errors) {
        toastrStore.trigger({
          message: `${error.key} - ${error.value}`,
          background: 'bg-error-500',
          classes: 'text-white',
        });
      }
    }
  }

  async function updateUser(): Promise<void> {
    const updatedUser: PutUserRequest = {
      id: $userId ?? 0,
      clientId: clientId,
      name,
      userName,
      email,
      isActive,
      userPhoto: userPhoto ? userPhoto.split(',')[1] : null,
      firstPage,
      isExemptedFromSync,
      permissions: permissions,
    };

    loading = true;

    try {
      await userStore.update(updatedUser);
      checkUserState();
    } catch (errors) {
      for (let error of errors) {
        toastrStore.trigger({
          message: `${error.key} - ${error.value}`,
          background: 'bg-error-500',
          classes: 'text-white',
        });
      }
    } finally {
      loading = false;
    }
  }

  function handleFileUpload(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      resizeImage(file, 64, 64)
        .then((resizedImage) => {
          userPhoto = resizedImage;
        })
        .catch((error) => {
          toastrStore.trigger({
            message: error,
            background: 'bg-error-500',
          });
        });
    }
  }

  async function resizeImage(file: File, width: number, height: number): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL(file.type));
          } else {
            reject('Failed to get canvas context.');
          }
        };
        img.src = event.target?.result as string;
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  }

  onMount(async () => {
    const result = await userStore.fetchPermissions();
    availablePermissions = result;

    await loadUserData();
  });
</script>

<div class="card my-6 bg-[#FAFAFC] p-6">
  <div class="mb-5 text-lg font-bold text-primary-500">Update User</div>

  <div class="grid grid-cols-4 gap-4 border border-[#EEEEEE] bg-[#FFFFFF] p-5 rounded-container-token">
    <!-- Avatar -->
    <div class="col-span-1 flex flex-col items-center justify-center">
      <label for="avatar" class="mb-2 text-sm text-gray-700">Avatar</label>
      <input type="file" id="avatar" accept="image/*" hidden on:change={handleFileUpload} />
      <label for="avatar" class="flex h-16 w-16 cursor-pointer items-center justify-center rounded-full border-2 border-gray-300">
        {#if userPhoto}
          <img src={userPhoto} alt="avatar" class="h-15 w-15 rounded-full object-cover" />
        {:else}
          <PlusCircle class="h-8 w-8 text-primary-500" />
        {/if}
      </label>
    </div>

    <!-- Name -->
    <div class="col-span-1 flex flex-col">
      <label for="name" class="mb-2 text-sm text-gray-700">Name</label>
      <input type="text" id="name" placeholder="" bind:value={name} class="input" />
    </div>

    <!-- Username -->
    <div class="col-span-1 flex flex-col">
      <label for="userName" class="mb-2 text-sm text-gray-700">Username</label>
      <input type="text" id="userName" placeholder="" bind:value={userName} class="input" />
    </div>

    <!-- Email -->
    <div class="col-span-1 flex flex-col">
      <label for="email" class="mb-2 text-sm text-gray-700">Email</label>
      <input type="email" id="email" placeholder="" bind:value={email} class="input" />
    </div>

    <!-- First Page -->
    <div class="col-span-1 flex flex-col">
      <label for="firstPage" class="mb-2 text-sm text-gray-700">First Page</label>
      <select id="firstPage" bind:value={firstPage} class="select">
        <option value="" disabled selected>Select First Page</option>
        {#each firstPageOptions as option}
          <option value={option}>{option}</option>
        {/each}
      </select>
    </div>

    <!-- Permissions -->
    <div class="col-span-1 flex flex-col">
      <label for="permissions" class="mb-2 text-sm text-gray-700">Permissions</label>
      <select id="permissions" bind:value={permissions} class="select" multiple>
        {#each availablePermissions as { key, value }}
          <option {value}>{value}</option>
        {/each}
      </select>
    </div>

    <!-- Is Exempted From Sync -->
    <div class="col-span-1 flex flex-col">
      <label for="isExemptedFromSync" class="mb-2 text-sm text-gray-700">Exempt from Sync</label>
      <SlideToggle name="isExemptedFromSync" bind:checked={isExemptedFromSync} active="bg-success-500" background="bg-secondary-600" />
    </div>

    <!-- Is Active -->
    <div class="col-span-1 flex flex-col">
      <label for="isActive" class="mb-2 text-sm text-gray-700">Active</label>
      <SlideToggle name="isActive" bind:checked={isActive} active="bg-success-500" background="bg-secondary-600" />
    </div>

    <!-- Action Buttons -->
    <div class="col-span-4 mt-4 flex justify-end space-x-4">
      <Button label="Cancel" size="medium" variant="outlined" onClick={() => dispatch('userUpdated')} />
      <Button label="Update User" colour="primary" size="medium" onClick={updateUser} disabled={loading} />
    </div>
  </div>
</div>
